import { animate, style, transition, trigger } from '@angular/animations'
import { CommonModule } from '@angular/common'
import { ZardButtonComponent } from '@/shared/components/button'
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { NgIconComponent, provideIcons } from '@ng-icons/core'
import { Subject, takeUntil } from 'rxjs'
import { ThemeRepository } from '../../state/theme.repository'
import { Toast, ToastType } from './toast.model'
import { ToastService } from './toast.service'
import {
  lucideCheck,
  lucideInfo,
  lucideAlertCircle,
  lucideX
} from '@ng-icons/lucide'

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, NgIconComponent, ZardButtonComponent],
  providers: [
    provideIcons({
      lucideCheck,
      lucideInfo,
      lucideAlertCircle,
      lucideX
    })
  ],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-0.5rem)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'none' }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('150ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ToastComponent implements OnInit, OnDestroy {
  themeRepository = inject(ThemeRepository)
  toastService = inject(ToastService)

  toasts = signal<Toast[]>([])
  unsubscribe = new Subject<void>()

  ngOnInit(): void {
    this.themeRepository.toasts$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(toasts => this.toasts.set(toasts))
  }

  removeToast(toast: Toast) {
    this.toastService.removeToast(toast.id)
  }

  getToastStyle(toast: Toast): {
    icon: string
    titleColor: string
    borderColor: string
    bgColor: string
  } {
    const theme = {
      [ToastType.SUCCESS]: {
        icon: 'lucideCheck',
        titleColor: 'text-green-700',
        borderColor: 'border-green-200',
        bgColor: 'bg-green-50'
      },
      [ToastType.INFO]: {
        icon: 'lucideInfo',
        titleColor: 'text-sky-700',
        borderColor: 'border-sky-200',
        bgColor: 'bg-sky-50'
      },
      [ToastType.WARNING]: {
        icon: 'lucideAlertCircle',
        titleColor: 'text-amber-700',
        borderColor: 'border-amber-200',
        bgColor: 'bg-amber-50'
      },
      [ToastType.DANGER]: {
        icon: 'lucideAlertCircle',
        titleColor: 'text-red-700',
        borderColor: 'border-red-200',
        bgColor: 'bg-red-50'
      }
    }

    return theme[toast.type]
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }
}
